// src/context/CartContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

import {storageKey} from '../services/buyApi';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage or empty array
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem(storageKey('ventes-buy-cart-v3'));
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey('ventes-buy-cart-v3'), JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    setCartItems((prev) => {
      // Create a unique key for products with variants
      const cartItemKey = product.selectedVariants 
        ? `${product.id}-${JSON.stringify(product.selectedVariants)}`
        : product.id;
      
      const existing = prev.find((item) => {
        const itemKey = item.selectedVariants 
          ? `${item.id}-${JSON.stringify(item.selectedVariants)}`
          : item.id;
        return itemKey === cartItemKey;
      });
      
      if (existing) {
        return prev.map((item) => {
          const itemKey = item.selectedVariants 
            ? `${item.id}-${JSON.stringify(item.selectedVariants)}`
            : item.id;
          return itemKey === cartItemKey
            ? { ...item, quantity: item.quantity + quantity }
            : item;
        });
      } else {
        return [...prev, { ...product, quantity, cartItemKey }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== productId));
  };

  const incrementQty = (productId) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === productId ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const decrementQty = (productId) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === productId && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((acc, item) => acc + (item.price || 0) * item.quantity, 0);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((acc, item) => acc + item.quantity, 0);
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item.id === productId);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        incrementQty,
        decrementQty,
        clearCart,
        getCartTotal,
        getCartItemsCount,
        isInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

